// [목적] notification 도메인 공유 zod 스키마 + DTO — BE/FE 동일 계약
// [이유] fe-be-gap-tier3 G10 — NotificationsPage 실데이터 훅이 BE 응답을 zod 파싱 후 사용(ADR-T3-3/§6-5).
//         @gangwoncanvas/types 이관으로 FE 별도 정의 불필요.
// [사이드 이팩트] notificationListQuerySchema: cursorLimitQuerySchema 패턴 재사용. tab 3종(all|social|system).
//                content 는 nullable — 소셜 알림은 NULL(ADR-T3-3), system/badge 만 문구 적재.
// [수정 시 고려사항] type 종류 추가 시 notificationTypeSchema + DDL CHECK + 엔티티 @Check 동기 갱신 필수.

import { z } from 'zod';

/** 알림 타입 enum — DDL CHECK 및 엔티티 @Check 와 동기 */
export const notificationTypeSchema = z.enum([
  'like',
  'fork',
  'follow',
  'comment',
  'system',
  'badge',
]);

export type NotificationType = z.infer<typeof notificationTypeSchema>;

/** 알림 탭 enum — ADR-T3-2: social/system/all */
export const notificationTabSchema = z.enum(['all', 'social', 'system']);

export type NotificationTab = z.infer<typeof notificationTabSchema>;

/**
 * GET /users/me/notifications 쿼리 파라미터.
 * tab: all(기본)=전체, social=like|fork|follow|comment, system=system|badge.
 * cursor/limit: 키셋 커서 페이지네이션.
 */
export const notificationListQuerySchema = z.object({
  tab: notificationTabSchema.optional().default('all'),
  cursor: z.string().max(500).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});

export type NotificationListQuery = z.infer<typeof notificationListQuerySchema>;

/**
 * 알림 단건 응답 아이템.
 * content: system/공지 전용(소셜은 null), FE 가 type+actor_handle+target 으로 문구 조립(ADR-T3-3).
 */
export const notificationItemSchema = z.object({
  id: z.string().uuid(),
  type: notificationTypeSchema,
  actorId: z.string().uuid().nullable(),
  actorHandle: z.string().nullable(),
  targetId: z.string().uuid().nullable(),
  targetType: z.string().nullable(),
  /** system/공지 알림 문구. 소셜 알림은 null(ADR-T3-3 — FE 조립) */
  content: z.string().nullable(),
  meta: z.record(z.unknown()),
  readAt: z.string().nullable(),
  createdAt: z.string(),
});

export type NotificationItem = z.infer<typeof notificationItemSchema>;

/**
 * GET /users/me/notifications 응답 data 필드.
 * unreadCount: 현재 페이지 한정이 아닌 본인 read_at IS NULL 전체 수.
 */
export const notificationListResponseSchema = z.object({
  items: z.array(notificationItemSchema),
  nextCursor: z.string().nullable(),
  unreadCount: z.number().int().nonnegative(),
});

export type NotificationListResponse = z.infer<typeof notificationListResponseSchema>;

/** POST /users/me/notifications/read-all 응답 */
export const notificationReadAllResponseSchema = z.object({
  affected: z.number().int().nonnegative(),
});

export type NotificationReadAllResponse = z.infer<typeof notificationReadAllResponseSchema>;
